(() => {
  const normalize = (value) => value.replace(/\s+/g, ' ').trim().toLowerCase();

  const findAnchor = (body, text) => {
    const wanted = normalize(text);
    if (!body || !wanted) return null;

    return Array.from(body.querySelectorAll('p, h2, h3, h4, li, blockquote'))
      .find((element) => normalize(element.textContent).includes(wanted));
  };

  const rememberOrigin = (node) => {
    if (!node || node.dataset.clArtOriginSaved === 'true') return;
    node._clArtOrigin = { parent: node.parentNode, next: node.nextSibling };
    node.dataset.clArtOriginSaved = 'true';
  };

  const insertAtOrigin = (node, parent, next) => {
    if (!parent || !node) return;
    if (next && next.parentNode === parent) parent.insertBefore(node, next);
    else parent.appendChild(node);
  };

  const restoreOrigin = (node) => {
    if (!node?._clArtOrigin) return;
    const { parent, next } = node._clArtOrigin;
    insertAtOrigin(node, parent, next);
  };

  const insertRestoreMarker = (parent, before) => {
    const marker = document.createComment('cl-art-restore');
    parent.insertBefore(marker, before ?? null);
    return marker;
  };

  const restoreNodeBeforeMarker = (parent, marker, node) => {
    if (parent && marker && node) parent.insertBefore(node, marker);
  };

  const restoreNodesBeforeMarker = (parent, marker, nodes) => {
    nodes.forEach((node) => restoreNodeBeforeMarker(parent, marker, node));
  };

  const unwrapChildrenBeforeMarker = (parent, marker, container) => {
    if (!parent || !marker || !container) return;
    while (container.firstChild) parent.insertBefore(container.firstChild, marker);
    container.remove();
  };

  const hiddenByLayout = new Set();
  const setLayoutHidden = (node, hidden) => {
    if (!node) return;
    if (hidden) {
      node.hidden = true;
      hiddenByLayout.add(node);
    } else if (hiddenByLayout.has(node)) {
      node.hidden = false;
      hiddenByLayout.delete(node);
    }
  };

  const clearLayoutHidden = () => {
    hiddenByLayout.forEach((node) => { node.hidden = false; });
    hiddenByLayout.clear();
  };

  const unwrapTextMediaRows = () => {
    document.querySelectorAll('.cl-art-text-media').forEach((row) => {
      const parent = row.parentNode;
      if (!parent) return;
      while (row.firstChild) parent.insertBefore(row.firstChild, row);
      row.remove();
    });
  };

  const removeEmptyResponsiveWrappers = () => {
    document.querySelectorAll('.cl-art-sidebar-callout, .cl-art-sidebar-media').forEach((wrapper) => {
      if (wrapper.childElementCount === 0) wrapper.remove();
    });
  };

  const restoreServiceCallout = (nodes) => {
    const { heading, label, figure, marker, parent } = nodes;
    if (!heading || !parent || !marker) return;
    restoreNodesBeforeMarker(parent, marker, [heading, label, figure]);
    marker.remove();
  };

  let layoutState = null;

  const clearGalleryStripCarousels = () => {
    document.querySelectorAll('.cl-art-gallery--strip[data-cl-art-strip-carousel]').forEach((gallery) => {
      gallery.querySelectorAll(':scope > figure').forEach((figure) => { figure.hidden = false; });
      gallery.classList.remove('cl-art-gallery--strip-carousel');
      delete gallery.dataset.clArtStripCarousel;
      const dots = gallery.nextElementSibling;
      if (dots?.classList.contains('cl-art-gallery__dots')) dots.remove();
    });
  };

  const initGalleryStripCarousels = (mobile) => {
    clearGalleryStripCarousels();
    if (!mobile) return;

    document.querySelectorAll('.cl-art-gallery--strip').forEach((gallery) => {
      const figures = [...gallery.querySelectorAll(':scope > figure')];
      if (figures.length < 2) return;

      gallery.dataset.clArtStripCarousel = 'true';
      gallery.classList.add('cl-art-gallery--strip-carousel');

      const dots = document.createElement('div');
      dots.className = 'cl-art-gallery__dots';
      dots.setAttribute('aria-hidden', 'true');

      let index = 0;
      const show = (next) => {
        index = (next + figures.length) % figures.length;
        figures.forEach((figure, i) => { figure.hidden = i !== index; });
        dots.querySelectorAll('span').forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });
      };

      figures.forEach((_, i) => {
        const dot = document.createElement('span');
        if (i === 0) dot.classList.add('is-active');
        dot.addEventListener('click', () => show(i));
        dots.append(dot);
      });

      gallery.after(dots);
      show(0);

      let touchStartX = null;
      gallery.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0]?.clientX ?? null;
      }, { passive: true });
      gallery.addEventListener('touchend', (event) => {
        const touchEndX = event.changedTouches[0]?.clientX;
        if (touchStartX == null || touchEndX == null) return;
        const delta = touchEndX - touchStartX;
        if (Math.abs(delta) < 40) return;
        show(index + (delta < 0 ? 1 : -1));
        touchStartX = null;
      }, { passive: true });
    });
  };

  const clearResponsiveLayout = () => {
    if (!layoutState) return;

    if (layoutState.serviceEndClone) {
      layoutState.serviceEndClone.remove();
    }
    if (layoutState.service) restoreServiceCallout(layoutState.service);
    if (layoutState.embossMobile?.wrap) {
      const { wrap, marker, parent } = layoutState.embossMobile;
      unwrapChildrenBeforeMarker(parent, marker, wrap);
      marker.remove();
    }
    if (layoutState.emboss?.nodes) {
      const { nodes, marker, parent } = layoutState.emboss;
      restoreNodesBeforeMarker(parent, marker, nodes);
      marker.remove();
    }
    if (layoutState.retailFigures) {
      layoutState.retailFigures.forEach(({ figure, marker, parent }) => {
        restoreNodeBeforeMarker(parent, marker, figure);
        marker.remove();
      });
    }
    if (layoutState.infographic?.moved && layoutState.infographic.figure) {
      const { figure, marker, parent } = layoutState.infographic;
      restoreNodeBeforeMarker(parent, marker, figure);
      marker.remove();
    }
    if (layoutState.springInfographic?.figure) {
      restoreOrigin(layoutState.springInfographic.figure);
    }
    if (layoutState.bookFeatures?.node) {
      restoreOrigin(layoutState.bookFeatures.node);
    }
    if (layoutState.mobileReview?.review) {
      const { review, replacement } = layoutState.mobileReview;
      if (replacement?.parentNode) replacement.replaceWith(review);
      else restoreOrigin(review);
    }

    document.querySelectorAll('.cl-art-products[data-cl-art-placement]').forEach(restoreOrigin);
    clearGalleryStripCarousels();
    unwrapTextMediaRows();
    removeEmptyResponsiveWrappers();
    clearLayoutHidden();
    layoutState = null;
  };

  const initAddToCart = (scope) => {
    scope.querySelectorAll('[data-cl-art-form]').forEach((form) => {
      if (form.dataset.clArtReady === 'true') return;
      form.dataset.clArtReady = 'true';

      form.addEventListener('submit', (event) => {
        if (!window.FormData || !window.Promise) return;
        const button = form.querySelector('button[type="submit"]');
        const label = form.querySelector('[data-cl-art-add-label]');
        const message = form.querySelector('[data-cl-art-message]');
        if (!button || button.disabled || button.dataset.clArtBusy === 'true') return;
        const defaultLabel = label ? label.textContent.trim() : 'Add to cart';

        event.preventDefault();
        button.dataset.clArtBusy = 'true';
        button.disabled = true;
        if (message) message.textContent = '';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: new FormData(form),
        })
          .then((response) => response.json().then((data) => {
            if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart.');
            return data;
          }))
          .then((item) => {
            if (label) label.textContent = 'Added ✓';
            form.dispatchEvent(new CustomEvent('product:added', {
              bubbles: true,
              detail: { variant: item, quantity: 1 },
            }));
            window.setTimeout(() => {
              if (label) label.textContent = defaultLabel;
              button.disabled = false;
              delete button.dataset.clArtBusy;
            }, 1800);
          })
          .catch((error) => {
            if (message) message.textContent = error.message || 'Could not add to cart.';
            button.disabled = false;
            delete button.dataset.clArtBusy;
          });
      });
    });
  };

  document.querySelectorAll('[data-cl-art-youtube]').forEach((source) => {
    const id = source.dataset.clArtYoutube;
    const target = document.querySelector(`.cl-art-yt[data-id="${CSS.escape(id)}"]`);
    const facade = source.querySelector('[data-cl-yt]');
    if (!target || !facade) return;
    target.replaceWith(facade);
    source.remove();
  });

  document.querySelectorAll('[data-cl-art-audio]').forEach((template) => {
    const audioBody = template.closest('.cl-art-article__main')?.querySelector('.cl-art-article__body');
    const label = Array.from(audioBody?.querySelectorAll('p') || [])
      .find((paragraph) => normalize(paragraph.textContent).startsWith('listen to this post'));
    if (!label) return;
    label.after(template.content.cloneNode(true));
    template.remove();
  });

  const article = document.querySelector('.cl-art-article');
  const body = article?.querySelector('.cl-art-article__body');
  const main = article?.querySelector('.cl-art-article__main');
  const header = article?.querySelector('.cl-art-article__header');
  const sidebar = article?.querySelector('.cl-art-article__sidebar');
  const trending = sidebar?.querySelector('.cl-art-article__trending-title');
  const trendingList = sidebar?.querySelector('.cl-art-article__trending');

  const intro = document.createElement('div');
  intro.className = 'cl-art-article__intro';
  while (body?.firstElementChild?.tagName === 'P') {
    intro.append(body.firstElementChild);
  }
  if (intro.childElementCount) header?.append(intro);

  if (article?.dataset.clArtHandle === 'how-chromalabel-works-with-hims-hers-telehealth') {
    const title = header?.querySelector('h1');
    const breakNode = title?.querySelector('br');
    if (breakNode) {
      const subtitle = document.createElement('span');
      subtitle.className = 'cl-art-title-subtitle';
      while (breakNode.nextSibling) subtitle.append(breakNode.nextSibling);
      breakNode.replaceWith(subtitle);
    }
  }

  body?.querySelectorAll('.cl-art-empty-product').forEach((node) => node.remove());

  body?.querySelectorAll('blockquote').forEach((quote) => {
    quote.dataset.clArtReview = 'true';
    quote.classList.add('cl-art-review-quote');
    if (!quote.querySelector('.cl-art-review-quote__stars')) {
      const stars = document.createElement('div');
      stars.className = 'cl-art-review-quote__stars';
      stars.setAttribute('aria-hidden', 'true');
      stars.textContent = '★★★★★';
      quote.prepend(stars);
    }
  });

  if (body && !body.dataset.clArtSectioned) {
    const nodes = Array.from(body.childNodes);
    const sections = [];
    let current = document.createElement('div');
    current.className = 'cl-art-body-card';
    nodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'H2' && current.childNodes.length) {
        sections.push(current);
        current = document.createElement('div');
        current.className = 'cl-art-body-card';
      }
      current.append(node);
    });
    if (current.childNodes.length) sections.push(current);
    if (sections.length) {
      sections.forEach((section) => body.append(section));
      if (sections.length > 1) body.dataset.clArtSectioned = 'true';
    }
  }

  Array.from(body?.querySelectorAll('h2') || []).forEach((heading) => {
    if (!/5 desk organization ideas/i.test(heading.textContent)) return;
    const figure = heading.previousElementSibling;
    if (figure?.tagName === 'FIGURE') figure.classList.add('cl-art-figure--bleed');
  });

  body?.querySelectorAll('figure').forEach((figure) => {
    const img = figure.querySelector('img');
    const alt = img?.getAttribute('alt') || '';
    const prev = figure.previousElementSibling;
    if (prev?.tagName === 'FIGCAPTION' || (prev?.tagName === 'P' && prev.classList.contains('cl-art-figure-caption'))) return;
    if (alt && figure.nextElementSibling?.tagName === 'P' && figure.nextElementSibling.textContent.trim() === alt.trim()) {
      figure.nextElementSibling.remove();
    }
  });

  const mfgLinks = body?.querySelectorAll('a[href*="top-5-products-for-logistics"]');
  if (mfgLinks?.length) {
    mfgLinks.forEach((link, index) => {
      link.classList.add('cl-art-mfg-cta');
      if (index > 0) link.remove();
    });
    const sidebarEl = article?.querySelector('.cl-art-article__sidebar');
    const primary = body?.querySelector('.cl-art-mfg-cta');
    if (sidebarEl && primary && !window.matchMedia('(max-width: 767px)').matches) {
      sidebarEl.append(primary.cloneNode(true));
    }
  }

  document.querySelectorAll('[data-cl-art-form-anchor]').forEach((form) => {
    const anchor = findAnchor(body, form.dataset.clArtFormAnchor || '');
    if (anchor) anchor.before(form);
  });

  const calloutHeadingStarts = [
    'why hospitals and gpo',
    'from the mailbag',
    'the 5 step plan',
    'more color-code dots',
    'but no matter the product',
    'where can you use our labels',
    'the importance of an organized medical office',
    'a simple demo',
    'what are dissolvable washable labels',
    'purge regularly',
    'deep clean those books',
    'and why does the nicu test',
    'because if this tape works',
    'contact us if you have questions',
    'are you ready to save time',
    'in any case, keeping each element',
    'in any case, keeping your medical practice',
  ];
  body?.querySelectorAll('p').forEach((paragraph) => {
    const text = paragraph.textContent.trim();
    if (text.startsWith('"') && text.endsWith('"')) {
      paragraph.classList.add('cl-art-pullquote');
    }
    const letters = text.replace(/[^A-Za-z]+/g, '');
    if (letters.length > 8 && text.length < 110 && letters === letters.toUpperCase()) {
      paragraph.classList.add('cl-art-section-label');
    }
    const normalized = normalize(text);
    if (calloutHeadingStarts.some((start) => normalized.startsWith(start))) {
      paragraph.classList.add('cl-art-callout-heading');
    }
  });

  const serviceFigures = Array.from(body?.querySelectorAll('figure') || [])
    .filter((figure) => figure.querySelector('img[src*="vikcustomerservice"]'));
  serviceFigures.forEach((figure) => figure.classList.add('cl-art-service-card'));

  const serviceHeading = Array.from(body?.querySelectorAll('h2') || [])
    .find((heading) => normalize(heading.textContent).startsWith('we can help'));
  const serviceLabel = serviceHeading?.nextElementSibling;
  const serviceFigure = serviceLabel?.nextElementSibling;

  const placeKlaviyoForm = () => {
    const form = document.querySelector('.cl-art-article__form');
    if (!form || form.dataset.clArtPlaced === 'true') return;
    const anchorText = form.dataset.clArtFormAnchor || '';
    const anchor = findAnchor(body, anchorText);
    if (anchor) {
      anchor.after(form);
    } else if (body?.lastElementChild) {
      body.lastElementChild.append(form);
    }
    form.dataset.clArtPlaced = 'true';
  };

  const applyResponsiveLayout = (mobile) => {
    clearResponsiveLayout();
    layoutState = {};

    const staticEndCallout = body?.querySelector('.cl-art-body-end-callout');
    if (serviceHeading && serviceLabel && serviceFigure?.tagName === 'FIGURE') {
      const parent = serviceHeading.parentNode;
      const marker = parent ? insertRestoreMarker(parent, serviceHeading) : null;
      const fromStaticEnd = Boolean(staticEndCallout?.contains(serviceHeading));
      if (mobile) {
        if (!fromStaticEnd) {
          setLayoutHidden(serviceHeading, true);
          setLayoutHidden(serviceLabel, true);
          serviceFigures.forEach((figure) => setLayoutHidden(figure, true));
        } else if (staticEndCallout) {
          main?.querySelector('.cl-art-article__mobile-products-hook')?.before(staticEndCallout);
        }
        marker?.remove();
      } else if (trending && parent && marker) {
        const callout = document.createElement('aside');
        callout.className = 'cl-art-sidebar-callout';
        trending.before(callout);
        callout.append(serviceHeading, serviceLabel, serviceFigure);
        layoutState.service = { heading: serviceHeading, label: serviceLabel, figure: serviceFigure, marker, parent };
        if (fromStaticEnd && staticEndCallout && !staticEndCallout.childElementCount) {
          staticEndCallout.remove();
        } else if (!fromStaticEnd) {
          const endCallout = document.createElement('aside');
          endCallout.className = 'cl-art-sidebar-callout cl-art-body-end-callout';
          endCallout.append(
            serviceHeading.cloneNode(true),
            serviceLabel.cloneNode(true),
            serviceFigure.cloneNode(true),
          );
          main?.querySelector('.cl-art-article__mobile-products-hook')?.before(endCallout);
          layoutState.serviceEndClone = endCallout;
        }
      } else {
        marker?.remove();
      }
    }

    document.querySelectorAll('[data-cl-art-mobile-review]').forEach((template) => {
      if (!mobile) return;
      const review = body?.querySelector('blockquote[data-cl-art-review="true"]');
      if (!review) return;
      rememberOrigin(review);
      const replacement = template.content.cloneNode(true);
      review.replaceWith(replacement);
      layoutState.mobileReview = { review, replacement };
    });

    if (article?.dataset.clArtHandle === 'how-to-emboss-stickers-with-a-stamp-chromalabel-dots') {
      const heading = Array.from(body?.querySelectorAll('h2') || [])
        .find((element) => normalize(element.textContent).startsWith('see what kurt did'));
      if (heading) {
        const parent = heading.parentNode;
        const marker = parent ? insertRestoreMarker(parent, heading) : null;
        if (mobile && parent && marker) {
          const nodes = [];
          let element = heading;
          while (element) {
            const next = element.nextElementSibling;
            nodes.push(element);
            if (next?.tagName === 'H2') break;
            element = next;
          }
          const wrap = document.createElement('div');
          wrap.className = 'cl-art-emboss-mobile-media';
          nodes.forEach((node) => wrap.append(node));
          const inlineProduct = body?.querySelector('.cl-art-products--featured');
          if (inlineProduct) inlineProduct.after(wrap);
          else main?.querySelector('.cl-art-article__mobile-products-hook')?.before(wrap);
          layoutState.embossMobile = { wrap, marker, parent };
        } else if (trending && parent && marker) {
          const media = document.createElement('aside');
          media.className = 'cl-art-sidebar-media';
          trending.before(media);
          const nodes = [];
          let element = heading;
          while (element) {
            const next = element.nextElementSibling;
            nodes.push(element);
            media.append(element);
            if (next?.tagName === 'H2') break;
            element = next;
          }
          layoutState.emboss = { nodes, marker, parent };
        } else {
          marker?.remove();
        }
      }
    }

    if (!mobile && article?.dataset.clArtHandle === 'strategies-for-color-coding-in-retail-stores' && trending) {
      const media = document.createElement('aside');
      media.className = 'cl-art-sidebar-media';
      trending.before(media);
      const retailFigures = [];
      Array.from(body?.querySelectorAll('figure') || []).forEach((figure) => {
        const parent = figure.parentNode;
        if (!parent) return;
        const marker = insertRestoreMarker(parent, figure);
        rememberOrigin(figure);
        media.append(figure);
        retailFigures.push({ figure, marker, parent });
      });
      layoutState.retailFigures = retailFigures;
    }

    if (!mobile && new Set([
      'how-to-keep-labels-on-tricky-hard-to-label-surfaces',
      'chromalabel-dots-used-by-u-s-automotive-industry',
    ]).has(article?.dataset.clArtHandle)) {
      Array.from(body?.querySelectorAll('figure') || []).slice(1).forEach((figure) => {
        const copy = figure.previousElementSibling;
        if (!copy || copy.tagName !== 'P') return;
        const row = document.createElement('div');
        row.className = 'cl-art-text-media';
        copy.before(row);
        row.append(copy, figure);
      });
    }

    const infographicHandles = new Set([
      'a-guide-to-organizing-your-office-supply-closet',
      '6-practical-tips-and-tricks-for-a-successful-yard-sale',
      'how-to-improve-workplace-productivity-through-organization',
    ]);
    if (infographicHandles.has(article?.dataset.clArtHandle)) {
      const infographic = Array.from(body?.querySelectorAll('figure') || []).at(-1);
      if (infographic) {
        const parent = infographic.parentNode;
        const marker = parent ? insertRestoreMarker(parent, infographic) : null;
        if (mobile) {
          marker?.remove();
          setLayoutHidden(infographic, true);
        } else if (trending && parent && marker) {
          rememberOrigin(infographic);
          const media = document.createElement('aside');
          media.className = 'cl-art-sidebar-media';
          media.append(infographic);
          trending.before(media);
          layoutState.infographic = { figure: infographic, marker, parent, moved: true };
        } else {
          marker?.remove();
        }
      }
    }

    document.querySelectorAll('.cl-art-products[data-cl-art-placement]').forEach((products) => {
      const placement = products.dataset.clArtPlacement;
      const inSidebar = placement === 'sidebar' || placement === 'responsive_sidebar';
      products.classList.toggle('cl-art-products--in-sidebar', !mobile && inSidebar);
      products.classList.toggle('cl-art-products--in-article', placement === 'inline' || (placement === 'responsive_sidebar' && mobile));
      products.hidden = false;

      if (placement === 'inline' || (placement === 'responsive_sidebar' && mobile)) {
        const anchor = findAnchor(body, products.dataset.clArtAnchor || '');
        if (anchor) {
          anchor[products.dataset.clArtAnchorPosition === 'before' ? 'before' : 'after'](products);
        } else {
          const hook = main?.querySelector('.cl-art-article__mobile-products-hook');
          if (hook) hook.before(products);
          else if (body) body.append(products);
        }
        return;
      }

      if (inSidebar) {
        if (mobile) {
          main?.querySelector('.cl-art-article__mobile-products-hook')?.before(products);
        } else if (products.dataset.clArtSidebarPosition === 'before_trending' && trending) {
          trending.before(products);
        } else if (trendingList) {
          trendingList.after(products);
        } else if (trending) {
          trending.before(products);
        } else if (sidebar) {
          sidebar.append(products);
        }
      }
    });

    if (article?.dataset.clArtHandle === 'the-ultimate-list-for-spring-cleaning-your-business') {
      const infographic = Array.from(body?.querySelectorAll('figure') || []).at(-1);
      if (infographic) {
        rememberOrigin(infographic);
        if (mobile) {
          main?.querySelector('.cl-art-article__mobile-products-hook')?.before(infographic);
        } else if (sidebar) {
          sidebar.append(infographic);
        }
        layoutState.springInfographic = { figure: infographic };
      }
    }

    if (!mobile && article?.dataset.clArtHandle === 'helpful-tips-tools-repairing-book-spines') {
      const features = body?.querySelector('.cl-art-product-features');
      if (features && sidebar) {
        rememberOrigin(features);
        if (trendingList) trendingList.after(features);
        else sidebar.append(features);
        layoutState.bookFeatures = { node: features };
      }
    }

    placeKlaviyoForm();
    initGalleryStripCarousels(mobile);
    initAddToCart(document);
  };

  let layoutMediaQuery = null;
  let productBlockCount = 0;

  const bootLayout = () => {
    document.querySelectorAll('.cl-art-products[data-cl-art-placement]').forEach(rememberOrigin);
    if (!layoutMediaQuery) {
      layoutMediaQuery = window.matchMedia('(max-width: 767px)');
      layoutMediaQuery.addEventListener('change', (event) => {
        applyResponsiveLayout(event.matches);
      });
    }
    applyResponsiveLayout(layoutMediaQuery.matches);
  };

  const scheduleBoot = () => {
    const run = () => { bootLayout(); };
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(run);
    else run();
  };

  window.clArtRefreshLayout = scheduleBoot;

  const watchLateProductSections = () => {
    const countBlocks = () => document.querySelectorAll('.cl-art-products[data-cl-art-placement]').length;
    productBlockCount = countBlocks();
    new MutationObserver(() => {
      const next = countBlocks();
      if (next > productBlockCount) {
        productBlockCount = next;
        scheduleBoot();
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      scheduleBoot();
      watchLateProductSections();
    });
  } else {
    scheduleBoot();
    watchLateProductSections();
  }

  document.dispatchEvent(new CustomEvent('cl-art:ready'));
})();
